import { graphql } from "./generated/index.js"

// Queries ======================================================================

// A tag page: the tag, its place in the hierarchy (ancestors, parent and
// siblings, children) and the records tagged with it. A record can carry the
// tag on more than one row (the aggregate tags and a named tagging field), so
// each tagging's own id comes back for a remove by row.
export const fetchTagBySlug = graphql(`
  query fetchTagBySlug($slug: String!) {
    tags(where: { slug: { _eq: $slug } }, limit: 1) {
      ...TagValueFields
      ancestors {
        ...TagValueFields
      }
      parent {
        ...TagValueFields
        children {
          ...TagValueFields
        }
      }
      children {
        ...TagValueFields
      }
      records {
        id
        record {
          ...RecordFields
        }
      }
    }
  }
`)

// Every tag, without its records (there are a lot).
export const fetchAllTags = graphql(`
  query fetchAllTags {
    tags(order_by: { name: asc }) {
      ...TagValueFields
    }
  }
`)

export const fetchTagsByIds = graphql(`
  query fetchTagsByIds($ids: [Int!]!) {
    tags(where: { id: { _in: $ids } }, order_by: { name: asc }) {
      ...TagValueFields
      ancestors {
        ...TagValueFields
      }
    }
  }
`)

// Tags whose name or parent's name matches `$search` (an ILIKE pattern),
// parents first.
export const searchTags = graphql(`
  query searchTags($search: String!) {
    tags(
      where: { _or: [{ name: { _ilike: $search } }, { parent: { name: { _ilike: $search } } }] }
      order_by: [
        { parent: { name: asc_nulls_first } }
        { parent_id: asc_nulls_first }
        { name: asc }
      ]
      limit: 100
    ) {
      id
      name
      slug
      color
      parent_id
      ancestors {
        id
        name
        slug
        color
      }
    }
  }
`)

// Location tags that look like duplicates of each other, grouped under their
// root country — the location-tag dedupe view. Site-admin only.
export const fetchLocationTagFuzzyGroups = graphql(`
  query fetchLocationTagFuzzyGroups {
    location_tag_fuzzy_groups(order_by: { root_country_name: asc }) {
      root_country_id
      root_country_name
      tag_ids
      names
      paths
    }
  }
`)

// Mutations ====================================================================

// `object` as a whole variable, not field-by-field, so `slug` can be genuinely
// ABSENT when the caller does not supply one. That is what lets the
// set_default_tag_slug trigger fill it. An explicit `slug: null` would not
// work: Hasura's column parser rejects a null for a NOT NULL column before the
// INSERT, so the trigger never runs (same reasoning as createRecordNested).
export const insertTag = graphql(`
  mutation insertTag($object: tags_insert_input!) {
    insert_tags_one(object: $object) {
      ...TagValueFields
    }
  }
`)

export const insertTags = graphql(`
  mutation insertTags($objects: [tags_insert_input!]!) {
    insert_tags(objects: $objects) {
      returning {
        ...TagValueFields
      }
    }
  }
`)

// The tag plus its immediate parent — enough to show where a re-parented tag
// landed without pulling children or every tagged record.
export const updateTag = graphql(`
  mutation updateTag($id: Int!, $_set: tags_set_input!) {
    update_tags_by_pk(pk_columns: { id: $id }, _set: $_set) {
      ...TagValueFields
      parent {
        ...TagValueFields
      }
    }
  }
`)

export const deleteTag = graphql(`
  mutation deleteTag($id: Int!) {
    delete_tags_by_pk(id: $id) {
      id
    }
  }
`)

export const deleteTags = graphql(`
  mutation deleteTags($ids: [Int!]!) {
    delete_tags(where: { id: { _in: $ids } }) {
      affected_rows
      returning {
        id
      }
    }
  }
`)

// Fold one tag into another: merge_tags (postgres/functions/merge_tags.sql)
// moves the source's taggings and children onto the target and deletes the
// source. It refuses a merge into the source's own descendant.
export const mergeTags = graphql(`
  mutation mergeTags($sourceId: Int!, $targetId: Int!) {
    merge_tags(args: { p_source_id: $sourceId, p_target_id: $targetId }) {
      ...TagValueFields
    }
  }
`)

// Mark a group of tags as not-duplicates, so the dedup view stops suggesting it.
export const dismissTagGroup = graphql(`
  mutation dismissTagGroup($tagIds: [Int!]!) {
    insert_dismissed_tag_groups_one(object: { tag_ids: $tagIds }) {
      id
    }
  }
`)
